/**
 * Agent-skill config — one managed agent's activation, schedule, template and
 * run history on its own sub-page (`/skills/:agentType`).
 *
 * This is the ClientAgentsPage logic SCOPED TO A SINGLE agent type: the same
 * activation toggle, schedule picker, "Run now", deactivate-confirm, 10s poll
 * and run-history — but only for the `:agentType` from the URL, resolved
 * against the registry (`available_types`). An unknown/removed type renders a
 * "not found" state back to the Agents hub. Agents v3: no chat mode, no
 * per-agent skill uploads (the Skills tab owns those), a KPI-targets note.
 *
 * Everything is scoped to the GLOBAL active brand (switched from the
 * bottom-left sidebar button). Reading is gated on `portal.reports.review`
 * (the whole surface); every write is additionally gated on
 * `portal.settings.manage`, matching the API. A failed load renders an error +
 * retry, never a lying empty state.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import SubPageInnerContentHolder from '../../../components/subPageContentHolder/SubPageInnerContentHolder';
import LoadingSpinner from '../../../ui/loadingSpinner/LoadingSpinner';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import Popup from '../../../ui/popup/Popup';
import { useCan } from '../../../lib/rbac';
import { setMessage } from '../../../store/reducers/appSlice';
import type { RootState } from '../../../store/store';
import AgentCard, {
  type BusyAction,
} from '../../client-reports/agents/AgentCard';
import AgentRunHistory from '../../client-reports/agents/AgentRunHistory';
import AgentSettingsCard from './AgentSettingsCard';
import KpiTargetsNote from './KpiTargetsNote';
import AgentOutputViewer from './AgentOutputViewer';
import AgentTemplateCard from '../../client-reports/agents/AgentTemplateCard';
import {
  activateAgent,
  AGENT_RUNS_DEFAULT_LIMIT,
  deactivateAgent,
  listAgents,
  listAgentRuns,
  runAgentNow,
  updateAgent,
  type AgentSchedule,
  type AgentView,
  type RunView,
  type TypeView,
} from '../../../lib/api/client-reports/client-agents-api';
import {
  agentRunBlocker,
  isValidCronShape,
  mergeRunPages,
  nextRunPhrase,
  runBlockerMessage,
  shouldPollRuns,
} from '../../client-reports/agents/agent-helpers';
import '../../client-reports/client-reports.scss';

/** How often the runs re-poll while one is running/generating (contract: 10s). */
const POLL_INTERVAL_MS = 10000;

const AgentSkillConfig = () => {
  const dispatch = useDispatch();
  const { agentType = '' } = useParams();
  /** Where "back" leads: the Agents hub with THIS agent's drawer open again. */
  const drawerPath = `/agents?agent=${encodeURIComponent(agentType)}`;
  const canRead = useCan('portal.reports.review');
  // Every mutation on this surface (activate, schedule, deactivate, run) is
  // `portal.settings.manage` on the API — mirror that here so a reviewer sees
  // the configuration read-only instead of buttons that 403.
  const canManage = useCan('portal.settings.manage');

  // Operate on the GLOBAL active brand (switched from the bottom-left sidebar
  // button) instead of an in-page brand selector.
  const activeShopSlug = useSelector(
    (s: RootState) => s.user?.profile?.active_shop_slug || ''
  );
  const activeShopName = useSelector(
    (s: RootState) =>
      (s.user?.profile?.all_shops ?? []).find(
        (shop: any) => shop?.slug === s.user?.profile?.active_shop_slug
      )?.name || ''
  );

  const { t } = useTranslation();
  const [agents, setAgents] = useState<AgentView[]>([]);
  const [types, setTypes] = useState<TypeView[]>([]);
  const [runs, setRuns] = useState<RunView[]>([]);
  /** Whether the server holds runs older than the ones loaded ("Load more"). */
  const [runsHasMore, setRunsHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  /** Brand whose data is actually in state — guards cross-brand renders. */
  const [loadedSlug, setLoadedSlug] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState<Record<string, BusyAction | null>>({});
  /** The agent type awaiting deactivate confirmation, or null. */
  const [confirmOff, setConfirmOff] = useState<{
    type: TypeView;
    agent: AgentView;
  } | null>(null);
  /** The run whose findings-mode HTML output is open in the sandboxed viewer. */
  const [viewingRun, setViewingRun] = useState<RunView | null>(null);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const toast = useCallback(
    (message: string, messageType: 'success' | 'error' | 'info') =>
      dispatch(setMessage({ message, messageType })),
    [dispatch]
  );

  const setBusyFor = (agent_type: string, action: BusyAction | null) =>
    setBusy((b) => ({ ...b, [agent_type]: action }));

  const load = useCallback(
    async (slug: string) => {
      if (!slug) return;
      setLoading(true);
      setLoadError(null);
      try {
        const [index, page] = await Promise.all([
          listAgents(slug),
          listAgentRuns(slug, {
            agent_type: agentType,
            limit: AGENT_RUNS_DEFAULT_LIMIT,
          }),
        ]);
        setAgents(index.agents);
        setTypes(index.available_types);
        setRuns(page.data);
        setRunsHasMore(page.meta.has_more);
        setLoadedSlug(slug);
      } catch (err: any) {
        const msg =
          err?.status === 403
            ? t(
                'AgentConfigNoBrandAccess',
                'You do not have access to that brand.'
              )
            : err?.message ||
              t(
                'AgentConfigLoadFailed',
                'Failed to load the agent for this brand.'
              );
        setLoadError(msg);
        dispatch(setMessage({ message: msg, messageType: 'error' }));
        setAgents([]);
        setTypes([]);
        setRuns([]);
        setRunsHasMore(false);
        setLoadedSlug(null);
      } finally {
        setLoading(false);
      }
    },
    // `t` is left out: a language switch must not re-fetch the agent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, agentType]
  );

  useEffect(() => {
    // Don't hit the review-gated endpoint for a user who can't view agents —
    // it 403s and the surface already renders the no-permission message.
    if (canRead && activeShopSlug) load(activeShopSlug);
  }, [canRead, activeShopSlug, load]);

  /** Silent refresh used by the poller — a blip must not wipe the page. */
  const refresh = useCallback(
    async (slug: string) => {
      try {
        const [index, page] = await Promise.all([
          listAgents(slug),
          listAgentRuns(slug, {
            agent_type: agentType,
            limit: AGENT_RUNS_DEFAULT_LIMIT,
          }),
        ]);
        setAgents(index.agents);
        setTypes(index.available_types);
        // Fold the fresh first page over what is loaded — pages the user pulled
        // in via "Load more" survive the poll (`runsHasMore` refers to beyond
        // the LOADED rows, so a first-page poll never touches it).
        setRuns((prev) => mergeRunPages(page.data, prev));
      } catch {
        /* transient — keep the last known state and try again next tick */
      }
    },
    [agentType]
  );

  /** "Load more" — one older page via the before-cursor, appended below. */
  const handleLoadMore = useCallback(async () => {
    if (!activeShopSlug || loadingMore || runs.length === 0) return;
    setLoadingMore(true);
    try {
      const before = runs[runs.length - 1].created_at;
      const page = await listAgentRuns(activeShopSlug, {
        agent_type: agentType,
        limit: AGENT_RUNS_DEFAULT_LIMIT,
        before,
      });
      setRuns((prev) => {
        const seen = new Set(prev.map((r) => r.id));
        return [...prev, ...page.data.filter((r) => !seen.has(r.id))];
      });
      setRunsHasMore(page.meta.has_more);
    } catch (err: any) {
      dispatch(
        setMessage({
          message:
            err?.message ||
            t('AgentConfigLoadMoreFailed', 'Failed to load more runs.'),
          messageType: 'error',
        })
      );
    } finally {
      setLoadingMore(false);
    }
  }, [activeShopSlug, loadingMore, runs, agentType, dispatch, t]);

  /**
   * After a template upload/remove the agents' `template` refs (and any
   * re-pointed deployments) are stale — re-pull the index quietly.
   */
  const handleTemplateChanged = useCallback(async () => {
    if (!activeShopSlug) return;
    try {
      const index = await listAgents(activeShopSlug);
      setAgents(index.agents);
      setTypes(index.available_types);
    } catch {
      /* the template card already reported its own success/failure */
    }
  }, [activeShopSlug]);

  /**
   * A settings save re-provisions the config-hashed agent and re-points this
   * brand's active deployment — the AgentView (schedule/next-run) can shift, so
   * re-pull the index quietly. The settings card owns its own re-pull + toast.
   */
  const handleSettingsChanged = useCallback(async () => {
    if (!activeShopSlug) return;
    try {
      const index = await listAgents(activeShopSlug);
      setAgents(index.agents);
      setTypes(index.available_types);
    } catch {
      /* the settings card already reported its own success/failure */
    }
  }, [activeShopSlug]);

  const isPolling = useMemo(() => shouldPollRuns(runs), [runs]);

  useEffect(() => {
    if (!isPolling || !activeShopSlug) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }
    pollRef.current = setInterval(() => {
      refresh(activeShopSlug);
    }, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [isPolling, activeShopSlug, refresh]);

  // Stop the poller on unmount.
  useEffect(
    () => () => {
      if (pollRef.current) clearInterval(pollRef.current);
    },
    []
  );

  const upsertAgent = (row: AgentView) =>
    setAgents((prev) => [
      ...prev.filter((a) => a.agent_type !== row.agent_type),
      row,
    ]);

  const handleToggle = async (
    type: TypeView,
    agent: AgentView | null,
    checked: boolean
  ) => {
    if (!canManage || !activeShopSlug || busy[type.agent_type]) return;
    if (!checked) {
      // Deactivation archives the deployment — confirm before doing it.
      if (agent) setConfirmOff({ type, agent });
      return;
    }
    setBusyFor(type.agent_type, 'activate');
    try {
      // POST creates-and-activates with the type defaults; a row that exists
      // but is switched off (is_active false) is re-enabled via PATCH.
      const row =
        agent && !agent.is_active
          ? await updateAgent(activeShopSlug, type.agent_type, {
              is_active: true,
            })
          : await activateAgent(activeShopSlug, {
              agent_type: type.agent_type,
            });
      upsertAgent(row);
      const phrase = nextRunPhrase(
        row.next_run_at,
        !row.last_run_at,
        undefined,
        t
      );
      toast(
        phrase
          ? t('AgentConfigActivatedNext', '{{agent}} activated — {{next}}.', {
              agent: type.label,
              next: phrase,
            })
          : t('AgentConfigActivated', '{{agent}} activated.', {
              agent: type.label,
            }),
        'success'
      );
    } catch (err: any) {
      toast(
        err?.message ||
          t('AgentConfigActivateFailed', 'Failed to activate {{agent}}.', {
            agent: type.label,
          }),
        'error'
      );
    } finally {
      setBusyFor(type.agent_type, null);
    }
  };

  const handleDeactivate = async () => {
    if (!confirmOff || !activeShopSlug) return;
    const { type } = confirmOff;
    setBusyFor(type.agent_type, 'deactivate');
    try {
      await deactivateAgent(activeShopSlug, type.agent_type);
      setAgents((prev) => prev.filter((a) => a.agent_type !== type.agent_type));
      setConfirmOff(null);
      toast(
        t(
          'AgentConfigSwitchedOff',
          '{{agent}} switched off — scheduled runs stop. Its settings are remembered if you switch it back on.',
          { agent: type.label }
        ),
        'success'
      );
    } catch (err: any) {
      toast(
        err?.message ||
          t('AgentConfigSwitchOffFailed', 'Failed to switch off {{agent}}.', {
            agent: type.label,
          }),
        'error'
      );
    } finally {
      setBusyFor(type.agent_type, null);
    }
  };

  const handleSaveSchedule = async (
    type: TypeView,
    agent: AgentView,
    schedule: AgentSchedule
  ) => {
    if (!canManage || !activeShopSlug || busy[type.agent_type]) return;
    if (!isValidCronShape(schedule.cron) || schedule.timezone === '') {
      toast(
        t('AgentConfigFixSchedule', 'Fix the schedule before saving.'),
        'error'
      );
      return;
    }
    setBusyFor(type.agent_type, 'schedule');
    try {
      const row = await updateAgent(activeShopSlug, type.agent_type, {
        schedule,
      });
      upsertAgent(row);
      toast(
        t('AgentConfigScheduleSaved', 'Schedule saved for {{agent}}.', {
          agent: type.label,
        }),
        'success'
      );
    } catch (err: any) {
      toast(
        err?.message ||
          t('AgentConfigScheduleSaveFailed', 'Failed to save the schedule.'),
        'error'
      );
    } finally {
      setBusyFor(type.agent_type, null);
    }
  };

  const handleRunNow = async (type: TypeView, agent: AgentView) => {
    if (!canManage || !activeShopSlug || busy[type.agent_type]) return;
    const blocker = agentRunBlocker(
      agent,
      runs.filter((r) => r.agent_type === type.agent_type)
    );
    if (blocker) {
      // The button is disabled in every one of these states, so this only
      // catches a stale click — say why instead of posting a guaranteed 4xx.
      toast(
        runBlockerMessage(
          blocker,
          t('AgentTheNamedAgent', 'the {{agent}}', { agent: type.label }),
          t
        ),
        'info'
      );
      return;
    }
    setBusyFor(type.agent_type, 'run');
    try {
      const { run_id } = await runAgentNow(activeShopSlug, type.agent_type);
      const nowIso = new Date().toISOString();
      // Optimistic 'running' row — the 10s poll takes over from here and
      // replaces it with the server's own view of the run.
      const optimistic: RunView = {
        id: run_id,
        agent_type: type.agent_type,
        trigger: 'manual',
        status: 'running',
        period_start: null,
        period_end: null,
        period_label: null,
        client_report_id: null,
        artifacts: [],
        summary: null,
        cost_cents: null,
        error_code: null,
        error: null,
        started_at: nowIso,
        finished_at: null,
        created_at: nowIso,
      };
      setRuns((prev) => [optimistic, ...prev.filter((r) => r.id !== run_id)]);
      toast(
        t(
          'AgentConfigRunStarted',
          '{{agent}} run started — this page updates as it progresses.',
          { agent: type.label }
        ),
        'success'
      );
    } catch (err: any) {
      const msg =
        err?.status === 409
          ? t(
              'AgentConfigRunInFlight',
              'A run is already in flight for the {{agent}}.',
              { agent: type.label }
            )
          : err?.status === 422
          ? t(
              'AgentConfigRunSwitchedOff',
              'The {{agent}} is switched off — switch it on first.',
              { agent: type.label }
            )
          : err?.message ||
            t('AgentConfigRunFailed', 'Failed to start the run.');
      toast(msg, 'error');
      if (err?.status === 409 || err?.status === 422) {
        // The server knows something this page doesn't — a run already in
        // flight (409) or the agent switched off elsewhere (422). Re-pull so
        // the true state lands on screen: the 409's hidden run becomes
        // visible and the 10s poll takes over from there.
        await refresh(activeShopSlug);
      }
    } finally {
      setBusyFor(type.agent_type, null);
    }
  };

  const agentByType = useMemo(() => {
    const map: Record<string, AgentView> = {};
    agents.forEach((a) => {
      map[a.agent_type] = a;
    });
    return map;
  }, [agents]);

  /** agent_type → label for run-history rows (registry + activated rows). */
  const typeLabels = useMemo(() => {
    const map: Record<string, string> = {};
    types.forEach((entry) => {
      map[entry.agent_type] = entry.label;
    });
    agents.forEach((a) => {
      if (a.label) map[a.agent_type] = a.label;
    });
    return map;
  }, [types, agents]);

  /** The registry entry for the `:agentType` in the URL, or null. */
  const type = useMemo(
    () => types.find((entry) => entry.agent_type === agentType) ?? null,
    [types, agentType]
  );

  const thisTypeRuns = useMemo(
    () => runs.filter((r) => r.agent_type === agentType),
    [runs, agentType]
  );

  if (!canRead) {
    return (
      <SubPageInnerContentHolder
        title={t('AgentConfigTitle', 'Agent')}
        backBtnLink={drawerPath}
      >
        <div className='client-reports__empty'>
          {t(
            'AgentsNoPermission',
            'You don\'t have permission to view agents. Ask an owner or manager for the "review reports" capability.'
          )}
        </div>
      </SubPageInnerContentHolder>
    );
  }

  return (
    <SubPageInnerContentHolder
      title={type ? type.label : t('AgentConfigTitle', 'Agent')}
      // Back returns to the Agents hub with this agent's drawer open — from
      // the config cards and from chat mode alike.
      backBtnLink={drawerPath}
    >
      <div className='client-agents'>
        {loading ? (
          <div style={{ marginTop: '3rem', textAlign: 'center' }}>
            <LoadingSpinner
              customLoadingMsg={t('AgentConfigLoading', 'Loading agent…')}
            />
          </div>
        ) : !activeShopSlug ? (
          <div className='client-reports__empty'>
            {t(
              'AgentsNoActiveBrand',
              'No active brand. Pick one from the button in the bottom-left.'
            )}
          </div>
        ) : loadError ? (
          <div className='client-agents__error'>
            {loadError}
            <div style={{ marginTop: '1.4rem' }}>
              <MainSquareButton
                type='button'
                value={t('Retry', 'Retry')}
                buttonType='light'
                normal
                onClick={() => load(activeShopSlug)}
              />
            </div>
          </div>
        ) : loadedSlug !== activeShopSlug ? (
          <div style={{ marginTop: '3rem', textAlign: 'center' }}>
            <LoadingSpinner
              customLoadingMsg={t('AgentConfigLoading', 'Loading agent…')}
            />
          </div>
        ) : !type ? (
          <div className='client-agents__error'>
            {t(
              'AgentConfigUnavailable',
              "That agent isn't available for your organisation."
            )}
            <div style={{ marginTop: '1.4rem' }}>
              {/* Unknown type → no drawer to reopen; plain hub. */}
              <Link className='client-agents__open-link' to='/agents'>
                {t('AgentConfigBackToAll', 'Back to all agents')}
              </Link>
            </div>
          </div>
        ) : type.kind === 'chat' ? (
          // A chat-kind registry entry has no page of its own any more
          // (Agents v3 removed "Chat with Agent").
          <div className='client-agents__error'>
            {t('AgentConfigNoPage', 'That agent has no configuration page.')}
            <div style={{ marginTop: '1.4rem' }}>
              <Link className='client-agents__open-link' to='/agents'>
                {t('AgentConfigBackToAll', 'Back to all agents')}
              </Link>
            </div>
          </div>
        ) : (
          <>
            {!canManage && (
              <p className='client-agents__readonly-note'>
                {t(
                  'AgentConfigReadOnly',
                  'Read-only — ask an owner or manager for the "manage portal settings" capability to switch agents on or off, change schedules or start a run.'
                )}
              </p>
            )}

            <AgentCard
              type={type}
              agent={agentByType[agentType] ?? null}
              runs={thisTypeRuns}
              canManage={canManage}
              busy={busy[agentType] ?? null}
              onToggle={handleToggle}
              onSaveSchedule={handleSaveSchedule}
              onRunNow={handleRunNow}
            />

            <KpiTargetsNote
              slug={activeShopSlug}
              brandName={activeShopName}
              agentLabel={type.label}
            />

            <AgentSettingsCard
              slug={activeShopSlug}
              agentType={agentType}
              canManage={canManage}
              brandName={activeShopName}
              onSettingsChanged={handleSettingsChanged}
            />

            {type.outputs.includes('pptx') && (
              <AgentTemplateCard
                slug={activeShopSlug}
                canManage={canManage}
                onTemplateChanged={handleTemplateChanged}
              />
            )}

            <AgentRunHistory
              runs={thisTypeRuns}
              agents={agents}
              typeLabels={typeLabels}
              hasMore={runsHasMore}
              loadingMore={loadingMore}
              onLoadMore={handleLoadMore}
              onOpenOutput={setViewingRun}
            />

            {viewingRun && type && (
              <AgentOutputViewer
                runId={viewingRun.id}
                title={`${type.label} — ${
                  viewingRun.period_label || t('AgentConfigRunWord', 'run')
                }`}
                onClose={() => setViewingRun(null)}
              />
            )}

            {isPolling && (
              <p className='client-agents__polling'>
                {t(
                  'AgentConfigAutoRefreshing',
                  'Auto-refreshing while a report is being generated…'
                )}
              </p>
            )}
          </>
        )}
      </div>

      {/* ---- deactivate confirmation ---- */}
      <Popup
        trigger={confirmOff !== null}
        setTrigger={() => {
          if (busy[confirmOff?.type.agent_type ?? ''] !== 'deactivate') {
            setConfirmOff(null);
          }
        }}
      >
        {confirmOff && (
          <div className='client-agents__confirm'>
            <h3 className='client-agents__confirm-title'>
              {t('AgentConfigSwitchOffTitle', 'Switch off the {{agent}}?', {
                agent: confirmOff.type.label,
              })}
            </h3>
            <p className='client-agents__confirm-body'>
              {t(
                'AgentConfigSwitchOffBody',
                "Scheduled runs for this brand stop immediately. Past reports and files are kept, and this brand's agent settings are remembered if you switch it back on."
              )}
            </p>
            <div className='client-agents__confirm-actions'>
              <MainSquareButton
                type='button'
                value={t('Cancel', 'Cancel')}
                buttonType='light'
                normal
                disabled={busy[confirmOff.type.agent_type] === 'deactivate'}
                onClick={() => setConfirmOff(null)}
              />
              <MainSquareButton
                type='button'
                value={
                  busy[confirmOff.type.agent_type] === 'deactivate'
                    ? t('AgentConfigSwitchingOff', 'Switching off…')
                    : t('AgentConfigSwitchOff', 'Switch off')
                }
                buttonType='danger'
                normal
                loading={busy[confirmOff.type.agent_type] === 'deactivate'}
                disabled={busy[confirmOff.type.agent_type] === 'deactivate'}
                onClick={handleDeactivate}
              />
            </div>
          </div>
        )}
      </Popup>
    </SubPageInnerContentHolder>
  );
};

export default AgentSkillConfig;
